import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import {
  ChartLine, SquaresFour, AirplaneTakeoff, Buildings, UsersThree, Triangle,
  SlidersHorizontal, FlowArrow, MapTrifold, ArrowsLeftRight, CurrencyCircleDollar, Gauge,
  FileText, SidebarSimple, MagnifyingGlass, Sun, Moon, Sparkle, CaretUpDown,
  SignOut, Gear, User, Play, type Icon,
} from '@phosphor-icons/react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import ChatInterface from '../copilot/ChatInterface';
import ConfidenceChip from './ConfidenceChip';
import ErrorBoundary from '../common/ErrorBoundary';

/* ──────────────────────────────────────────────────────────────
   Aipron DACAMS — AppShell (North Star "Capacity Operations Console")
   Collapsible rail + topbar + ⌘K command palette + CoPilot dock.
   Phase 1: chrome + re-homes existing pages; page internals unchanged.
   ────────────────────────────────────────────────────────────── */

type Lang = 'es' | 'en';
interface Bi { es: string; en: string; }
const tx = (b: Bi, l: Lang) => b[l];

interface NavItem {
  key: string;
  label: Bi;
  icon: Icon;
  /** Resolve the destination route; `pid` = current project id (or null). */
  to: (pid: string | null) => string;
  tag?: string;
}
interface NavGroup { label: Bi; items: NavItem[]; }

// In-project modules live under /projects/:id/<route>; with no active project the
// rail item falls back to the project list. Standalone modules (RunCap/AirCap/
// Optimizer) carry the project as ?projectId= so they stay scoped to it.
const inProject = (p: string | null, route: string) => (p ? `/projects/${p}/${route}` : '/analysis');
const withPid = (p: string | null, base: string) => (p ? `${base}?projectId=${p}` : base);

const NAV: NavGroup[] = [
  {
    label: { es: 'Análisis de capacidad', en: 'Capacity analysis' },
    items: [
      { key: 'overview', label: { es: 'Resumen del Análisis DACAMS', en: 'DACAMS Analysis Overview' }, icon: ChartLine, tag: 'P0', to: (p) => inProject(p, 'overview') },
      { key: 'apcas', label: { es: 'AP-CAM', en: 'AP-CAM' }, icon: SquaresFour, tag: 'P1', to: (p) => inProject(p, 'apcas') },
      { key: 'runcap', label: { es: 'RunCap', en: 'RunCap' }, icon: AirplaneTakeoff, tag: 'RRC', to: (p) => withPid(p, '/runcap') },
      { key: 'aircap', label: { es: 'AirCap', en: 'AirCap' }, icon: Buildings, tag: 'PTBC', to: (p) => withPid(p, '/aircap') },
      { key: 'adosa', label: { es: 'ADOSA', en: 'ADOSA' }, icon: UsersThree, tag: 'P2', to: (p) => inProject(p, 'adosa') },
      { key: 'equilibrium', label: { es: 'Equilibrio', en: 'Equilibrium' }, icon: Triangle, tag: 'P3·4', to: (p) => inProject(p, 'equilibrium') },
      { key: 'optimizer', label: { es: 'Optimizer · SVS', en: 'Optimizer · SVS' }, icon: SlidersHorizontal, tag: 'P5', to: (p) => withPid(p, '/optimizer') },
      { key: 'kpis', label: { es: 'KPIs', en: 'KPIs' }, icon: Gauge, to: (p) => inProject(p, 'kpis') },
      { key: 'mapas', label: { es: 'Mapas', en: 'Maps' }, icon: MapTrifold, to: (p) => inProject(p, 'mapas') },
    ],
  },
  {
    label: { es: 'Caso de negocio', en: 'Business case' },
    items: [
      { key: 'fts', label: { es: 'Simulación FTS', en: 'FTS simulation' }, icon: FlowArrow, to: () => '/fts' },
      { key: 'dashboard', label: { es: 'Escenarios de Capacidad', en: 'Capacity Scenarios' }, icon: ArrowsLeftRight, to: () => '/dashboard' },
      { key: 'economics', label: { es: 'Economics', en: 'Economics' }, icon: CurrencyCircleDollar, tag: 'IPS·AMSRI', to: (p) => inProject(p, 'economics') },
    ],
  },
  {
    label: { es: 'Entregable', en: 'Deliverable' },
    items: [
      { key: 'report', label: { es: 'Informe · APCAP', en: 'Report · APCAP' }, icon: FileText, tag: 'APCAP', to: () => '/reports' },
    ],
  },
];

const T = {
  search: { es: 'Buscar o ejecutar…', en: 'Search or run…' },
  palettePh: { es: 'Ir a un módulo o ejecutar una acción…', en: 'Go to a module or run an action…' },
  goto: { es: 'Ir a', en: 'Go to' },
  actions: { es: 'Acciones', en: 'Actions' },
  projects: { es: 'Proyectos', en: 'Projects' },
  runAnalysis: { es: 'Ejecutar análisis DACAMS', en: 'Run DACAMS analysis' },
  genReport: { es: 'Generar informe APCAP', en: 'Generate APCAP report' },
  toggleTheme: { es: 'Cambiar tema claro / oscuro', en: 'Toggle light / dark theme' },
  logout: { es: 'Cerrar sesión', en: 'Sign out' },
  settings: { es: 'Preferencias', en: 'Settings' },
  profile: { es: 'Mi perfil', en: 'My profile' },
  collapse: { es: 'Colapsar panel (⌘B)', en: 'Collapse rail (⌘B)' },
  copilot: { es: 'CoPilot', en: 'CoPilot' },
};

const RAIL_KEY = 'dacams_rail_collapsed';

const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage } = useLanguage();
  const lang = (language === 'en' ? 'en' : 'es') as Lang;

  const [railCollapsed, setRailCollapsed] = useState<boolean>(() => localStorage.getItem(RAIL_KEY) === '1');
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [userMenu, setUserMenu] = useState(false);
  const paletteInputRef = useRef<HTMLInputElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const currentProjectId = useMemo(() => {
    const m = matchPath('/projects/:id/*', location.pathname) || matchPath('/projects/:id', location.pathname);
    const id = m?.params?.id;
    if (id && id !== 'new') return id;
    // Standalone module pages (RunCap/AirCap/Optimizer) carry the project as a
    // query param so the rail stays scoped to it while off the /projects tree.
    const qp = new URLSearchParams(location.search).get('projectId');
    return qp || null;
  }, [location.pathname, location.search]);

  const toggleRail = useCallback(() => {
    setRailCollapsed((c) => { localStorage.setItem(RAIL_KEY, c ? '0' : '1'); return !c; });
  }, []);

  const openPalette = useCallback(() => { setPaletteOpen(true); setQuery(''); }, []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  useEffect(() => {
    if (paletteOpen) setTimeout(() => paletteInputRef.current?.focus(), 20);
  }, [paletteOpen]);

  // Global keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && k === 'k') { e.preventDefault(); setPaletteOpen((o) => !o); }
      else if ((e.metaKey || e.ctrlKey) && k === 'b') { e.preventDefault(); toggleRail(); }
      else if (e.key === 'Escape') { setPaletteOpen(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleRail]);

  // Close user menu on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenu(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const isActive = (item: NavItem) => {
    // Compare on the path only; some destinations carry a ?projectId= query.
    const to = item.to(currentProjectId).split('?')[0];
    if (to === '/analysis') return location.pathname === '/analysis';
    return location.pathname === to || location.pathname.startsWith(to + '/');
  };

  const activeItem = useMemo(() => {
    for (const g of NAV) for (const it of g.items) if (isActive(it)) return it;
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, currentProjectId]);

  const go = (to: string) => { navigate(to); closePalette(); };

  const userInitials = user?.full_name
    ? user.full_name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  // Command palette entries
  const paletteGoto = NAV.flatMap((g) => g.items).map((it) => ({
    key: it.key, label: tx(it.label, lang), tag: it.tag, icon: it.icon, run: () => go(it.to(currentProjectId)),
  }));
  const paletteActions = [
    { key: 'a-report', label: tx(T.genReport, lang), icon: FileText, kb: '⌘R', run: () => go('/reports') },
    { key: 'a-run', label: tx(T.runAnalysis, lang), icon: Play, kb: '⌘↵', run: () => go(currentProjectId ? `/projects/${currentProjectId}` : '/analysis') },
    { key: 'a-theme', label: tx(T.toggleTheme, lang), icon: theme === 'dark' ? Sun : Moon, kb: '⌘⇧L', run: () => { toggleTheme(); closePalette(); } },
  ];
  const q = query.trim().toLowerCase();
  const fGoto = paletteGoto.filter((c) => !q || c.label.toLowerCase().includes(q) || c.key.includes(q) || (c.tag || '').toLowerCase().includes(q));
  const fActions = paletteActions.filter((c) => !q || c.label.toLowerCase().includes(q));

  return (
    <div className={`dcs-shell${railCollapsed ? ' dcs-rc' : ''}${copilotOpen ? ' dcs-cp' : ''}`}>
      <style>{SHELL_CSS}</style>

      {/* ============ RAIL ============ */}
      <aside className="dcs-rail">
        <div className="dcs-brand" onClick={() => navigate('/')} role="button" tabIndex={0}>
          <img
            className="dcs-logo"
            src={theme === 'dark' ? '/img/Logo_dark.png' : '/img/Logo_light.png'}
            alt="Aipron DACAMS"
          />
          <img
            className="dcs-logo-icon"
            src={theme === 'dark' ? '/img/Logo_icon_dark.png' : '/img/Logo_icon_light.png'}
            alt=""
            aria-hidden="true"
          />
        </div>

        <div className="dcs-switcher" role="button" tabIndex={0} onClick={() => navigate('/analysis')}
             title={tx(T.projects, lang)}>
          <span className="dcs-dot" />
          <span className="dcs-sw-meta">
            <div className="dcs-sw-p">{currentProjectId ? `Proyecto` : tx(T.projects, lang)}</div>
            <div className="dcs-sw-a">{currentProjectId ? currentProjectId.slice(0, 8) : 'Aipron DACAMS'}</div>
          </span>
          <CaretUpDown className="dcs-chev" size={14} />
        </div>

        <nav className="dcs-nav">
          {NAV.map((g) => (
            <React.Fragment key={g.label.en}>
              <div className="dcs-grp"><b>{tx(g.label, lang)}</b></div>
              {g.items.map((it) => {
                const IconC = it.icon;
                const on = isActive(it);
                return (
                  <div key={it.key} className={`dcs-item${on ? ' dcs-on' : ''}`}
                       onClick={() => navigate(it.to(currentProjectId))} role="button" tabIndex={0}
                       title={tx(it.label, lang)}>
                    <IconC size={16} weight={on ? 'fill' : 'regular'} />
                    <span className="dcs-lb">{tx(it.label, lang)}</span>
                    {it.tag && <span className="dcs-tag">{it.tag}</span>}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </nav>

        <div className="dcs-railfoot" ref={userMenuRef}>
          <div className="dcs-user" onClick={() => setUserMenu((v) => !v)} role="button" tabIndex={0}>
            <span className="dcs-av">{userInitials}</span>
            <span className="dcs-user-t">{user?.full_name || 'User'}<div className="dcs-user-s">{user?.email || 'Aipron'}</div></span>
          </div>
          {userMenu && (
            <div className="dcs-usermenu">
              <button onClick={() => { setUserMenu(false); navigate('/profile'); }}><User size={15} />{tx(T.profile, lang)}</button>
              <button onClick={() => { setUserMenu(false); navigate('/settings'); }}><Gear size={15} />{tx(T.settings, lang)}</button>
              <button className="dcs-danger" onClick={() => { logout(); navigate('/login'); }}><SignOut size={15} />{tx(T.logout, lang)}</button>
            </div>
          )}
        </div>
      </aside>

      {/* ============ TOPBAR ============ */}
      <header className="dcs-topbar">
        <button className="dcs-icbtn" onClick={toggleRail} title={tx(T.collapse, lang)}><SidebarSimple size={18} /></button>
        <div className="dcs-crumb">
          <b>Aipron DACAMS</b>
          {activeItem && <><span className="dcs-sep">▸</span><span>{tx(activeItem.label, lang)}</span></>}
        </div>
        <div className="dcs-spacer" />
        <ConfidenceChip lang={lang} />
        <button className="dcs-cmdk" onClick={openPalette}>
          <MagnifyingGlass size={14} />
          <span>{tx(T.search, lang)}</span>
          <kbd>⌘K</kbd>
        </button>
        <div className="dcs-seg">
          <button className={lang === 'es' ? 'dcs-son' : ''} onClick={() => setLanguage('es')}>ES</button>
          <button className={lang === 'en' ? 'dcs-son' : ''} onClick={() => setLanguage('en')}>EN</button>
        </div>
        <button className="dcs-icbtn" onClick={toggleTheme} title={tx(T.toggleTheme, lang)}>
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>
        <button className={`dcs-icbtn${copilotOpen ? ' dcs-act' : ''}`} onClick={() => setCopilotOpen((o) => !o)} title="CoPilot"><Sparkle size={18} /></button>
      </header>

      {/* ============ WORKSPACE (existing pages, unchanged) ============ */}
      {/* Keyed by route so a page crash shows a readable error (not a black
          screen) and navigating away resets the boundary. */}
      <main className="dcs-work">
        <ErrorBoundary key={location.pathname} showDetails>{children}</ErrorBoundary>
      </main>

      {/* ============ COMMAND PALETTE ============ */}
      {paletteOpen && (
        <div className="dcs-backdrop dcs-open" onClick={(e) => { if (e.target === e.currentTarget) closePalette(); }}>
          <div className="dcs-palette" role="dialog" aria-label="Command palette">
            <div className="dcs-p-in">
              <MagnifyingGlass size={17} />
              <input ref={paletteInputRef} value={query} onChange={(e) => setQuery(e.target.value)}
                     placeholder={tx(T.palettePh, lang)} autoComplete="off"
                     onKeyDown={(e) => { if (e.key === 'Enter') { const first = fGoto[0] || fActions[0]; first?.run(); } }} />
              <kbd>esc</kbd>
            </div>
            <div className="dcs-p-list">
              {fGoto.length > 0 && <div className="dcs-gl">{tx(T.goto, lang)}</div>}
              {fGoto.map((c) => { const I = c.icon; return (
                <div key={c.key} className="dcs-cmd" onClick={c.run}><I size={15} /><span>{c.label}</span>{c.tag && <span className="dcs-kb">{c.tag}</span>}</div>
              ); })}
              {fActions.length > 0 && <div className="dcs-gl">{tx(T.actions, lang)}</div>}
              {fActions.map((c) => { const I = c.icon; return (
                <div key={c.key} className="dcs-cmd" onClick={c.run}><I size={15} /><span>{c.label}</span><span className="dcs-kb">{c.kb}</span></div>
              ); })}
              {fGoto.length === 0 && fActions.length === 0 && <div className="dcs-empty">—</div>}
            </div>
            <div className="dcs-callout">Aipron DACAMS · Capacity Operations Console</div>
          </div>
        </div>
      )}

      {/* CoPilot — docked as the shell's right column (a third grid track opens
          when it is toggled). Falls back to a fixed overlay on narrow screens. */}
      {copilotOpen && (
        <aside className="dcs-copilot">
          <ChatInterface projectId={currentProjectId ?? ''} isOpen={copilotOpen} onClose={() => setCopilotOpen(false)} docked />
        </aside>
      )}
    </div>
  );
};

/* ── Shell chrome CSS (prefixed dcs-, uses the app CSS variables) ── */
const SHELL_CSS = `
.dcs-shell{display:grid;grid-template-columns:224px 1fr;grid-template-rows:50px 1fr;height:100vh;
  transition:grid-template-columns .18s ease;background:var(--bg);color:var(--text-primary);
  font-family:var(--font-sans);}
.dcs-shell.dcs-rc{grid-template-columns:58px 1fr;}
.dcs-shell.dcs-cp{grid-template-columns:224px 1fr 380px;}
.dcs-shell.dcs-rc.dcs-cp{grid-template-columns:58px 1fr 380px;}
.dcs-rail{grid-row:1/3;background:var(--surface);border-right:1px solid var(--border);
  display:flex;flex-direction:column;min-height:0;overflow:hidden;}
.dcs-topbar{grid-column:2;background:var(--surface);border-bottom:1px solid var(--border);
  display:flex;align-items:center;gap:10px;padding:0 12px;}
.dcs-work{grid-column:2;overflow-y:auto;overflow-x:hidden;min-width:0;}
.dcs-copilot{grid-column:3;grid-row:1/3;background:var(--surface);border-left:1px solid var(--border);
  min-width:0;overflow:hidden;display:flex;flex-direction:column;}

.dcs-brand{display:flex;align-items:center;gap:9px;padding:14px 16px 10px;white-space:nowrap;cursor:pointer;}
/* Scale the wordmark to the rail width (height follows the aspect ratio), the way
   dragging an image's corner in Word/PowerPoint would. */
.dcs-brand .dcs-logo{width:100%;height:auto;object-fit:contain;display:block;flex:1 1 auto;min-width:0;}
.dcs-brand .dcs-logo-icon{height:34px;width:auto;object-fit:contain;display:none;flex:none;}

.dcs-switcher{margin:4px 12px 10px;padding:8px 10px;border:1px solid var(--border);border-radius:6px;
  background:var(--surface-raised);cursor:pointer;display:flex;align-items:center;gap:8px;white-space:nowrap;}
.dcs-switcher:hover{border-color:var(--border-strong);}
.dcs-switcher .dcs-dot{width:7px;height:7px;border-radius:50%;background:var(--good);flex:none;}
.dcs-sw-meta{min-width:0;overflow:hidden;}
.dcs-sw-p{font-size:12px;font-weight:600;}
.dcs-sw-a{font-size:10.5px;color:var(--text-tertiary);font-family:var(--font-mono);}
.dcs-switcher .dcs-chev{margin-left:auto;color:var(--text-tertiary);flex:none;}

.dcs-nav{flex:1;overflow-y:auto;padding:4px 8px;min-height:0;}
.dcs-grp{font-family:var(--font-mono);font-size:9.5px;letter-spacing:.14em;text-transform:uppercase;color:var(--text-tertiary);padding:12px 8px 5px;white-space:nowrap;}
.dcs-item{display:flex;align-items:center;gap:9px;padding:6px 8px;border-radius:5px;font-size:12.5px;color:var(--text-secondary);cursor:pointer;position:relative;user-select:none;white-space:nowrap;}
.dcs-item:hover{background:var(--accent-soft);color:var(--text-primary);}
.dcs-item svg{flex:none;opacity:.9;}
.dcs-item .dcs-tag{margin-left:auto;font-family:var(--font-mono);font-size:9px;color:var(--text-tertiary);border:1px solid var(--border);border-radius:3px;padding:0 4px;}
.dcs-item.dcs-on{background:var(--accent-soft);color:var(--text-primary);font-weight:600;}
.dcs-item.dcs-on::before{content:"";position:absolute;left:-8px;top:6px;bottom:6px;width:2px;background:var(--aipron-accent);border-radius:2px;}

.dcs-railfoot{border-top:1px solid var(--border);padding:8px;position:relative;}
.dcs-user{display:flex;align-items:center;gap:9px;padding:8px;white-space:nowrap;cursor:pointer;border-radius:5px;}
.dcs-user:hover{background:var(--surface-raised);}
.dcs-av{width:24px;height:24px;border-radius:50%;background:var(--border-strong);color:var(--text-primary);display:grid;place-items:center;font-size:10px;font-weight:700;flex:none;}
.dcs-user-t{font-size:11.5px;overflow:hidden;text-overflow:ellipsis;}
.dcs-user-s{color:var(--text-tertiary);font-size:10px;overflow:hidden;text-overflow:ellipsis;}
.dcs-usermenu{position:absolute;bottom:100%;left:8px;right:8px;margin-bottom:6px;background:var(--surface-raised);border:1px solid var(--border);border-radius:8px;box-shadow:var(--shadow-lg);overflow:hidden;z-index:30;}
.dcs-usermenu button{width:100%;display:flex;align-items:center;gap:9px;padding:9px 12px;background:transparent;border:0;color:var(--text-secondary);font-size:12.5px;cursor:pointer;font-family:var(--font-sans);text-align:left;}
.dcs-usermenu button:hover{background:var(--accent-soft);color:var(--text-primary);}
.dcs-usermenu .dcs-danger{color:var(--crit);}

/* collapsed rail */
.dcs-rc .dcs-brand{justify-content:center;padding:14px 0 10px;}
.dcs-rc .dcs-brand .dcs-logo{display:none;}
.dcs-rc .dcs-brand .dcs-logo-icon{display:block;}
.dcs-rc .dcs-switcher{justify-content:center;padding:8px;margin:4px 8px 10px;}
.dcs-rc .dcs-sw-meta,.dcs-rc .dcs-switcher .dcs-chev{display:none;}
.dcs-rc .dcs-grp{text-align:center;padding:12px 0 5px;font-size:8px;color:var(--border-strong);}
.dcs-rc .dcs-grp b{display:none;}
.dcs-rc .dcs-grp::after{content:"···";}
.dcs-rc .dcs-item{justify-content:center;padding:8px;}
.dcs-rc .dcs-item .dcs-lb,.dcs-rc .dcs-item .dcs-tag{display:none;}
.dcs-rc .dcs-item.dcs-on::before{left:-4px;}
.dcs-rc .dcs-user-t{display:none;}

/* topbar */
.dcs-crumb{display:flex;align-items:center;gap:8px;font-size:12.5px;color:var(--text-secondary);white-space:nowrap;}
.dcs-crumb b{color:var(--text-primary);font-family:var(--font-display);}
.dcs-crumb .dcs-sep{color:var(--text-tertiary);}
.dcs-spacer{flex:1;}
.dcs-cmdk{display:flex;align-items:center;gap:8px;padding:5px 9px;border:1px solid var(--border);border-radius:6px;background:var(--surface-raised);color:var(--text-tertiary);cursor:text;font-size:12px;min-width:210px;font-family:var(--font-sans);}
.dcs-cmdk:hover{border-color:var(--border-strong);}
.dcs-cmdk kbd{margin-left:auto;font-family:var(--font-mono);font-size:10px;border:1px solid var(--border);border-radius:3px;padding:1px 5px;color:var(--text-secondary);background:var(--surface);}
.dcs-icbtn{width:30px;height:30px;display:grid;place-items:center;border-radius:5px;color:var(--text-secondary);cursor:pointer;flex:none;background:transparent;border:0;}
.dcs-icbtn:hover{background:var(--accent-soft);color:var(--text-primary);}
.dcs-icbtn.dcs-act{background:var(--accent-soft);color:var(--aipron-accent);}
.dcs-seg{display:flex;border:1px solid var(--border);border-radius:5px;overflow:hidden;}
.dcs-seg button{background:transparent;border:0;color:var(--text-tertiary);font-family:var(--font-mono);font-size:10.5px;padding:5px 8px;cursor:pointer;}
.dcs-seg button.dcs-son{background:var(--accent-soft);color:var(--aipron-accent);}

/* command palette */
.dcs-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.42);display:flex;z-index:400;align-items:flex-start;justify-content:center;padding-top:12vh;}
.dcs-palette{width:min(560px,92vw);background:var(--surface-raised);border:1px solid var(--border-strong);border-radius:10px;overflow:hidden;box-shadow:var(--shadow-xl);}
.dcs-p-in{display:flex;align-items:center;gap:10px;padding:13px 15px;border-bottom:1px solid var(--border);color:var(--text-tertiary);}
.dcs-p-in input{flex:1;background:transparent;border:0;outline:0;color:var(--text-primary);font-family:var(--font-sans);font-size:15px;}
.dcs-p-in kbd{font-family:var(--font-mono);font-size:10px;color:var(--text-tertiary);}
.dcs-p-list{max-height:340px;overflow-y:auto;padding:6px;}
.dcs-gl{font-family:var(--font-mono);font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:var(--text-tertiary);padding:9px 10px 4px;}
.dcs-cmd{display:flex;align-items:center;gap:10px;padding:8px 10px;border-radius:6px;font-size:13px;cursor:pointer;color:var(--text-primary);}
.dcs-cmd:hover{background:var(--accent-soft);}
.dcs-cmd svg{color:var(--text-secondary);flex:none;}
.dcs-cmd .dcs-kb{margin-left:auto;font-family:var(--font-mono);font-size:10px;color:var(--text-tertiary);}
.dcs-empty{padding:18px;text-align:center;color:var(--text-tertiary);font-family:var(--font-mono);}
.dcs-callout{font-size:11px;color:var(--text-tertiary);font-family:var(--font-mono);text-align:center;padding:10px;border-top:1px solid var(--border);}

@media (max-width:900px){
  .dcs-shell,.dcs-shell.dcs-rc,.dcs-shell.dcs-cp,.dcs-shell.dcs-rc.dcs-cp{grid-template-columns:1fr;grid-template-rows:50px 1fr;}
  .dcs-rail{display:none;}
  .dcs-topbar,.dcs-work{grid-column:1;}
  /* No room to push a column on mobile — dock the CoPilot as a fixed overlay. */
  .dcs-copilot{position:fixed;top:0;right:0;bottom:0;width:min(420px,100vw);z-index:70;
    box-shadow:var(--shadow-xl);}
}
`;

export default AppShell;
